import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { MessageSquare, Mail, Send, X, Clock, CheckCircle, XCircle, AlertCircle, Phone, ExternalLink, Plus, Save } from 'lucide-react';
import { sendSMS, sendEmail, textToHtml, checkMessagingSubscription, openMailto } from './smsUtils';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function CustomerCommunications({ customer, job, companyId, companyName, userId, onClose }) {
  const { colors } = useTheme();
  const { showAlert, showSuccess, showError } = useModal();
  const [activeTab, setActiveTab] = useState('compose'); // 'compose' | 'history'
  const [channel, setChannel] = useState('sms');
  const [emailSubject, setEmailSubject] = useState('');
  const [charCount, setCharCount] = useState(0); // Just for display
  const [sending, setSending] = useState(false);
  const [communications, setCommunications] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [messagingStatus, setMessagingStatus] = useState({ hasSubscription: false, domainVerified: false, loading: true });
  const [selectedJob, setSelectedJob] = useState(job || null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const textareaRef = useRef(null);

  // Default quick templates (used if no custom templates exist)
  const defaultSmsTemplates = [
    { name: 'On My Way', message: 'Hi {customer_name}, this is your tech from {company_name}. I\'m on my way and should arrive in about 15 minutes!' },
    { name: 'Running Late', message: 'Hi {customer_name}, I\'m running about 30 minutes behind schedule. I apologize for the delay and will be there as soon as possible.' },
    { name: 'Job Complete', message: 'Hi {customer_name}, your service has been completed. Thank you for choosing us! Please let us know if you have any questions.' },
    { name: 'Reminder', message: 'Hi {customer_name}, this is a reminder about your upcoming appointment tomorrow. Please reply to confirm!' }
  ];

  const defaultEmailTemplates = [
    { name: 'Quote Follow-up', message: 'Hi {customer_name},\n\nI wanted to follow up on the quote we sent over. Please let me know if you have any questions or would like to schedule the work.\n\nBest regards,\n{company_name}' },
    { name: 'Invoice Sent', message: 'Hi {customer_name},\n\nYour invoice is attached. Please let us know if you have any questions.\n\nThank you for your business!\n{company_name}' }
  ];

  // Combine custom templates with defaults, custom takes priority
  const smsTemplates = customTemplates.filter(t => t.channel === 'sms').length > 0
    ? customTemplates.filter(t => t.channel === 'sms')
    : defaultSmsTemplates;

  const emailTemplates = customTemplates.filter(t => t.channel === 'email').length > 0
    ? customTemplates.filter(t => t.channel === 'email')
    : defaultEmailTemplates;

  // SMS and Email are always available - native fallback (sms: / mailto:) when no subscription
  const addons = {
    sms: { enabled: true },
    email: { enabled: true }
  };
  const hasApiSms = messagingStatus.hasSubscription;
  const hasApiEmail = messagingStatus.hasSubscription && messagingStatus.domainVerified;

  useEffect(() => {
    loadData();
  }, [customer?.id]);

  const loadData = async () => {
    if (!customer?.id) return;

    setLoadingHistory(true);
    try {
      // Load communications history
      const { data: comms } = await supabaseData
        .from('communications')
        .select('*, jobs(job_number)')
        .eq('customer_id', customer.id)
        .order('sent_at', { ascending: false })
        .limit(50);

      setCommunications(comms || []);

      // Load customer's jobs for job selector
      const { data: jobs } = await supabaseData
        .from('jobs')
        .select('id, job_number, job_type, status')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false })
        .limit(10);

      setCustomerJobs(jobs || []);

      // Check company's messaging subscription
      if (companyId) {
        const status = await checkMessagingSubscription(companyId);
        setMessagingStatus({
          hasSubscription: status.hasSubscription,
          domainVerified: status.domainVerified,
          subscription: status.subscription,
          tier: status.tier || 'shared', // 'shared' = Work Van number, 'dedicated' = own number
          loading: false
        });
      } else {
        setMessagingStatus({ hasSubscription: false, domainVerified: false, tier: 'shared', loading: false });
      }

      // Load custom message templates
      if (companyId) {
        // Load custom message templates
        const { data: templatesData } = await supabaseData
          .from('message_templates')
          .select('*')
          .eq('company_id', companyId)
          .order('category', { ascending: true })
          .order('name', { ascending: true });

        setCustomTemplates(templatesData || []);
      }
    } catch (error) {

    } finally {
      setLoadingHistory(false);
    }
  };

  const applyTemplate = (template) => {
    const fullName = customer?.name ||
      `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() ||
      'there';
    const firstName = fullName.split(' ')[0];
    const address = customer?.address || '';

    // Get job-related info if available
    const jobNumber = selectedJob?.job_number || job?.job_number || '';
    const appointmentDate = selectedJob?.scheduled_date || job?.scheduled_date || '';
    const appointmentTime = selectedJob?.scheduled_time || job?.scheduled_time || '';

    let filled = template.message
      // New variable format
      .replace(/{customer_name}/g, firstName)
      .replace(/{customer_full_name}/g, fullName)
      .replace(/{company_name}/g, companyName || 'Work Van')
      .replace(/{address}/g, address)
      .replace(/{job_number}/g, jobNumber)
      .replace(/{appointment_date}/g, appointmentDate)
      .replace(/{appointment_time}/g, appointmentTime)
      .replace(/{quote_total}/g, '') // Would need to be passed in
      .replace(/{invoice_total}/g, '') // Would need to be passed in
      // Legacy variable format (backwards compatibility)
      .replace(/{name}/g, firstName)
      .replace(/{company}/g, companyName || 'Work Van');

    if (textareaRef.current) {
      textareaRef.current.value = filled;
      setCharCount(filled.length);
    }
  };

  const handleSend = async () => {
    const message = textareaRef.current?.value || '';

    if (!message.trim()) {
      showAlert('Please enter a message', { title: 'Missing Message' });
      return;
    }

    if (!customer?.phone && channel === 'sms') {
      showAlert('Customer has no phone number', { title: 'No Phone Number' });
      return;
    }

    if (!customer?.email && channel === 'email') {
      showAlert('Customer has no email address', { title: 'No Email' });
      return;
    }

    setSending(true);
    try {
      if (channel === 'sms') {
        // No subscription - use native SMS via sms: URI
        if (!messagingStatus.hasSubscription) {
          const phone = customer.phone.replace(/\D/g, '');
          const formattedPhone = phone.length === 10 ? `+1${phone}` : `+${phone}`;
          // Use sms: URI - works on iOS, Android, and desktop
          // iOS uses & for body separator, Android uses ?
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const separator = isIOS ? '&' : '?';
          const smsUrl = `sms:${formattedPhone}${separator}body=${encodeURIComponent(message)}`;
          window.open(smsUrl, '_self');

          // Log to communications table as "native_sms"
          try {
            await supabaseData.from('communications').insert({
              company_id: companyId,
              customer_id: customer.id,
              type: 'sms',
              direction: 'outbound',
              message: message,
              status: 'sent_native',
              sent_at: new Date().toISOString(),
              sent_by: userId,
              job_id: selectedJob?.id || null
            });
          } catch (logErr) {
            // Non-critical - don't block the SMS
          }

          setSending(false);
          showSuccess('Message opened in your messaging app');
          return;
        }

        // Has subscription - send via Telnyx API
        let finalMessage = message;

        // Use real Telnyx SMS
        const result = await sendSMS({
          phone: customer.phone,
          message: finalMessage,
          customerId: customer.id,
          companyId: companyId,
          userId: userId,
          jobId: selectedJob?.id || null,
          templateId: null,
          sentBy: userId
        });

        if (!result.success) {
          if (result.needsSubscription) {
            showAlert('Messaging subscription required. Subscribe in Settings.', { title: 'Subscription Required' });
            return;
          }
          if (result.needsCredits) {
            showAlert(`Insufficient credits. You have ${result.creditsAvailable} credits but need ${result.creditsNeeded}. Purchase more in Settings.`, { title: 'Insufficient Credits' });
            return;
          }
          throw new Error(result.error);
        }

        showSuccess('SMS sent successfully!');
      } else {
        // Email - check if we should use in-app sending or mailto
        const subject = emailSubject || `Message from ${companyName || 'Your Service Provider'}`;

        // If no subscription OR no verified domain, use mailto
        if (!hasApiEmail) {
          // Use mailto fallback - opens user's email client
          openMailto({
            to: customer.email,
            subject: subject,
            body: message
          });

          // Log to communications table as "sent_native"
          try {
            await supabaseData.from('communications').insert({
              company_id: companyId,
              customer_id: customer.id,
              type: 'email',
              direction: 'outbound',
              message: message,
              status: 'sent_native',
              sent_at: new Date().toISOString(),
              sent_by: userId,
              job_id: selectedJob?.id || null
            });
          } catch (logErr) {
            // Non-critical
          }

          showSuccess('Message opened in your email app');
          setEmailSubject('');
        } else {
          // Send via Resend (subscription with verified domain)
          const result = await sendEmail({
            to: customer.email,
            subject: subject,
            text: message,
            html: textToHtml(message),
            customerId: customer.id,
            companyId: companyId,
            userId: userId,
            companyName: companyName,
            jobId: selectedJob?.id || null,
            sentBy: userId
          });

          if (!result.success) {
            if (result.needsSubscription || result.needsCredits) {
              // Fallback to mailto
              openMailto({
                to: customer.email,
                subject: subject,
                body: message
              });
              showAlert('Opening your email client instead...', { title: 'Email' });
              setEmailSubject('');
            } else {
              throw new Error(result.error || 'Failed to send email');
            }
          } else {
            showSuccess('Email sent successfully!');
            setEmailSubject('');
          }
        }
      }

      if (textareaRef.current) textareaRef.current.value = '';
      setCharCount(0);
      setActiveTab('history');
      loadData(); // Refresh history
    } catch (error) {

      showError(error.message);
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    const message = textareaRef.current?.value || '';
    if (!newTemplateName.trim()) {
      showAlert('Please enter a template name', { title: 'Missing Name' });
      return;
    }
    if (!message.trim()) {
      showAlert('Write a message first, then save it as a template', { title: 'No Message' });
      return;
    }

    setSavingTemplate(true);
    try {
      const { error } = await supabaseData.from('message_templates').insert({
        company_id: companyId,
        name: newTemplateName.trim(),
        message: message,
        channel: channel,
        is_default: false
      });

      if (error) throw error;

      // Reload templates
      const { data: templatesData } = await supabaseData
        .from('message_templates')
        .select('*')
        .eq('company_id', companyId)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      setCustomTemplates(templatesData || []);
      setNewTemplateName('');
      setShowSaveTemplate(false);
      showSuccess('Template saved!');
    } catch (error) {
      showError(error.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status?.includes('mock')) return <AlertCircle size={14} color="#f39c12" />;
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle size={14} color="#4ecca3" />;
      case 'sent_native':
        return <ExternalLink size={14} color="#3498db" />;
      case 'failed':
        return <XCircle size={14} color="#e74c3c" />;
      default:
        return <Clock size={14} color="#888" />;
    }
  };

  const templates = channel === 'sms' ? smsTemplates : emailTemplates;

  // Use portal to render outside parent component tree - prevents focus issues
  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '1rem'
    }}>
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid ${colors.border}`
        }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MessageSquare size={24} color={colors.accent} />
              Messages
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', color: colors.textMuted, fontSize: '0.9rem' }}>
              {customer?.name || `${customer?.first_name} ${customer?.last_name}`}
              {customer?.phone && ` • ${customer.phone}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${colors.border}`
        }}>
          <button
            onClick={() => setActiveTab('compose')}
            style={{
              flex: 1,
              padding: '1rem',
              background: activeTab === 'compose' ? colors.bgInput : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'compose' ? `2px solid ${colors.accent}` : '2px solid transparent',
              color: activeTab === 'compose' ? colors.text : colors.textMuted,
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <Send size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Compose
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              flex: 1,
              padding: '1rem',
              background: activeTab === 'history' ? colors.bgInput : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'history' ? `2px solid ${colors.accent}` : '2px solid transparent',
              color: activeTab === 'history' ? colors.text : colors.textMuted,
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <Clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
            History ({communications.length})
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {activeTab === 'compose' && (
            <div>
              {/* Channel Selector */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  Send via
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setChannel('sms')}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: channel === 'sms' ? '#3498db' : colors.bgInput,
                      border: 'none',
                      borderRadius: '8px',
                      color: channel === 'sms' ? '#fff' : colors.text,
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Phone size={18} />
                    SMS
                  </button>
                  <button
                    onClick={() => setChannel('email')}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: channel === 'email' ? '#9b59b6' : colors.bgInput,
                      border: 'none',
                      borderRadius: '8px',
                      color: channel === 'email' ? '#fff' : colors.text,
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Mail size={18} />
                    Email
                  </button>
                </div>
              </div>

              {/* Job Selector (optional) */}
              {customerJobs.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Link to job (optional)
                  </label>
                  <select
                    value={selectedJob?.id || ''}
                    onChange={(e) => {
                      const jobId = e.target.value;
                      setSelectedJob(jobId ? customerJobs.find(j => j.id === jobId) : null);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="">No specific job</option>
                    {customerJobs.map(j => (
                      <option key={j.id} value={j.id}>
                        {j.job_number} - {j.job_type || 'Service'} ({j.status})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Quick Templates */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  Quick templates
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {templates.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => applyTemplate(t)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: colors.bgHover,
                        border: 'none',
                        borderRadius: '6px',
                        color: colors.text,
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      {t.name}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: showSaveTemplate ? `${colors.accent}40` : 'transparent',
                      border: '1px dashed #666',
                      borderRadius: '6px',
                      color: colors.accent,
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Plus size={14} />
                    Save as Template
                  </button>
                </div>
                {showSaveTemplate && (
                  <div style={{
                    marginTop: '0.75rem',
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center'
                  }}>
                    <input
                      type="text"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="Template name..."
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.accent}`,
                        borderRadius: '6px',
                        color: colors.text,
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />
                    <button
                      onClick={handleSaveTemplate}
                      disabled={savingTemplate}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: colors.accent,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: savingTemplate ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Save size={14} />
                      {savingTemplate ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setShowSaveTemplate(false); setNewTemplateName(''); }}
                      style={{
                        padding: '0.5rem',
                        background: 'none',
                        border: 'none',
                        color: colors.textMuted,
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Email Subject Field */}
                {channel === 'email' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                      Subject
                    </label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder={`Message from ${companyName || 'Your Service Provider'}`}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>
                )}

                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  Message
                </label>
                <textarea
                  ref={textareaRef}
                  autoFocus
                  defaultValue=""
                  onInput={(e) => setCharCount(e.target.value.length)}
                  placeholder={`Type your ${channel === 'sms' ? 'text message' : 'email'}...`}
                  rows={channel === 'email' ? 8 : 4}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                    {channel === 'sms' && charCount > 160 &&
                      `${Math.ceil(charCount / 160)} segments`}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                    {charCount} characters
                  </span>
                </div>
              </div>

              {/* Info Box */}
              <div style={{
                background: channel === 'email'
                  ? (hasApiEmail ? '#9b59b620' : '#f39c1220')
                  : (hasApiSms ? '#4ecca320' : '#3498db20'),
                border: `1px solid ${channel === 'email'
                  ? (hasApiEmail ? '#9b59b6' : '#f39c12')
                  : (hasApiSms ? '#4ecca3' : '#3498db')}`,
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1.5rem',
                fontSize: '0.85rem',
                color: channel === 'email'
                  ? (hasApiEmail ? '#9b59b6' : '#f39c12')
                  : (hasApiSms ? '#4ecca3' : '#3498db')
              }}>
                {channel === 'email' ? (
                  hasApiEmail ? (
                    <>
                      <strong>Live Email:</strong> This email will be sent via Resend to {customer?.email}
                    </>
                  ) : (
                    <>
                      <ExternalLink size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                      <strong>Opens your email app</strong> with the message pre-filled to {customer?.email}
                    </>
                  )
                ) : (
                  hasApiSms ? (
                    <>
                      <strong>Live SMS:</strong> This message will be sent to {customer?.phone} from your dedicated number
                    </>
                  ) : (
                    <>
                      <ExternalLink size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                      <strong>Opens your messaging app</strong> with the message pre-filled to {customer?.phone}
                    </>
                  )
                )}
              </div>

              {/* Send Button */}
              {(() => {
                const isNative = (channel === 'sms' && !hasApiSms) || (channel === 'email' && !hasApiEmail);
                const buttonColor = isNative ? '#3498db' : '#4ecca3';
                const buttonLabel = sending ? 'Opening...' :
                  isNative ? (channel === 'sms' ? 'Open in Messages' : 'Open in Email') : 'Send Message';
                return (
                  <button
                    onClick={handleSend}
                    disabled={sending || charCount === 0}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: (sending || charCount === 0) ? colors.bgHover : buttonColor,
                      border: 'none',
                      borderRadius: '8px',
                      color: (sending || charCount === 0) ? colors.textMuted : colors.bgCard,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      cursor: (sending || charCount === 0) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {isNative ? <ExternalLink size={20} /> : <Send size={20} />}
                    {buttonLabel}
                  </button>
                );
              })()}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                  Loading history...
                </div>
              ) : communications.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 2rem',
                  background: colors.bgInput,
                  borderRadius: '12px',
                  border: `2px dashed ${colors.border}`
                }}>
                  <MessageSquare size={48} color={colors.textMuted} style={{ marginBottom: '1rem' }} />
                  <h3 style={{ color: colors.textMuted, margin: '0 0 0.5rem 0' }}>No messages yet</h3>
                  <p style={{ color: colors.textMuted, margin: 0 }}>
                    Send your first message to start the conversation
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {communications.map(comm => (
                    <div
                      key={comm.id}
                      style={{
                        background: colors.bgInput,
                        borderRadius: '12px',
                        padding: '1rem',
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {comm.type === 'sms' ?
                            <Phone size={16} color="#3498db" /> :
                            <Mail size={16} color="#9b59b6" />
                          }
                          <span style={{
                            color: comm.direction === 'outbound' ? '#4ecca3' : '#3498db',
                            fontSize: '0.85rem',
                            fontWeight: 'bold'
                          }}>
                            {comm.direction === 'outbound' ? 'Sent' : 'Received'}
                          </span>
                          {comm.jobs?.job_number && (
                            <span style={{
                              background: colors.bgHover,
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              color: colors.textMuted
                            }}>
                              {comm.jobs.job_number}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {getStatusIcon(comm.status)}
                          <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                            {comm.status === 'sent_native' ? 'via device' : comm.status?.replace('(mock)', '').trim()}
                          </span>
                        </div>
                      </div>

                      <p style={{
                        margin: '0 0 0.75rem 0',
                        color: colors.text,
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {comm.message}
                      </p>

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: colors.textMuted,
                        fontSize: '0.8rem'
                      }}>
                        <Clock size={12} />
                        {new Date(comm.sent_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
